/**
 * SiteTrackingProvider — mounts site-wide interaction capture (clicks, keys,
 * mouse moves) and streams it to Firestore in real time via ../utils/siteTracking.
 *
 * Drop-in reuse on another site:
 *   1. Copy src/utils/siteTracking.js + this file.
 *   2. Ensure ../firebase/firebaseConfig exports `db` and SiteContext exposes
 *      `firebaseSiteDocId` (or pass a `siteId` prop directly).
 *   3. Render <SiteTrackingProvider> anywhere inside the Router.
 *
 * Must live inside <Router> (it uses useLocation to log SPA page views).
 */
import { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSite } from './SiteContext';
import {
  startSiteTracking,
  stopSiteTracking,
  recordPageView,
} from '../utils/siteTracking';

const SiteTrackingContext = createContext({ enabled: false });

export const SiteTrackingProvider = ({ children, siteId: siteIdProp, enabled = true }) => {
  let resolvedSiteId = siteIdProp;
  try {
    const site = useSite();
    if (!resolvedSiteId) resolvedSiteId = site?.firebaseSiteDocId;
  } catch (_) {
    // No SiteProvider above us — rely on the explicit siteId prop.
  }

  const location = useLocation();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !resolvedSiteId) return undefined;
    startSiteTracking({ siteId: resolvedSiteId });
    startedRef.current = true;
    return () => {
      stopSiteTracking('provider_unmount');
      startedRef.current = false;
    };
  }, [enabled, resolvedSiteId]);

  // Log each SPA navigation (skip the very first render — session_start already logs it).
  const firstNavRef = useRef(true);
  useEffect(() => {
    if (!enabled || !startedRef.current) return;
    if (firstNavRef.current) {
      firstNavRef.current = false;
      return;
    }
    recordPageView(location.pathname + (location.search || ''));
  }, [enabled, location.pathname, location.search]);

  return (
    <SiteTrackingContext.Provider value={{ enabled: Boolean(enabled && resolvedSiteId), siteId: resolvedSiteId }}>
      {children}
    </SiteTrackingContext.Provider>
  );
};

export const useSiteTracking = () => useContext(SiteTrackingContext);

export default SiteTrackingContext;
